import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { FloatingPaths } from '@LandingFunctionalDisplay/BackgroundPaths';
import TopPanel from '@CommonFunctionalDisplay/TopPanel';
import Footer from '@CommonFunctionalDisplay/Footer';
import useSEO from '@Hooks/useSEO';
import './BlogsPage.scss';

const POSTS = [
    {
        tag: 'Product',
        tagColor: 'blue',
        title: 'How to Set Up Your First Support Form in 5 Minutes',
        excerpt: 'A step-by-step walkthrough of KaizIQ Resolve\'s form builder — from blank canvas to live ticket intake in under five minutes.',
        date: 'March 28, 2026',
        readTime: '4 min read',
        featured: true,
    },
    {
        tag: 'Best Practices',
        tagColor: 'green',
        title: 'WhatsApp Support: The Future of Customer Communication',
        excerpt: 'Why WhatsApp\'s 98% open rate makes it the most effective channel for ticket notifications — and how to make it work for your team.',
        date: 'March 18, 2026',
        readTime: '6 min read',
        featured: false,
    },
    {
        tag: 'Operations',
        tagColor: 'purple',
        title: 'SLA Management Best Practices for Small Teams',
        excerpt: 'Setting realistic SLAs, building escalation matrices, and keeping your team accountable without burning out.',
        date: 'March 10, 2026',
        readTime: '7 min read',
        featured: false,
    },
    {
        tag: 'Tips',
        tagColor: 'green',
        title: '5 Ways to Reduce Ticket Resolution Time by 40%',
        excerpt: 'Proven strategies — from intake form design to assignment automation — that consistently cut resolution time for support teams.',
        date: 'February 28, 2026',
        readTime: '5 min read',
        featured: false,
    },
    {
        tag: 'Case Study',
        tagColor: 'orange',
        title: 'How an EdTech Startup Cut Support Load by 60%',
        excerpt: 'Discover how a fast-growing education platform used KaizIQ Resolve to handle 10x ticket volume with the same team size.',
        date: 'February 14, 2026',
        readTime: '8 min read',
        featured: false,
    },
    {
        tag: 'Changelog',
        tagColor: 'blue',
        title: 'KaizIQ Resolve 2.0: What\'s New',
        excerpt: 'A deep dive into the biggest release in our history — WhatsApp replies, mobile app launch, escalation matrix, and 30+ smaller improvements.',
        date: 'January 31, 2026',
        readTime: '10 min read',
        featured: false,
    },
];

const TAG_COLORS = {
    blue:   { bg: 'rgba(26,111,181,0.18)',  text: '#3b9aee' },
    green:  { bg: 'rgba(5,150,105,0.18)',   text: '#10b981' },
    purple: { bg: 'rgba(124,58,237,0.18)',  text: '#a78bfa' },
    orange: { bg: 'rgba(234,88,12,0.18)',   text: '#fb923c' },
};

function Reveal({ children, className = '', delay = 0 }) {
    return (
        <motion.div className={className}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay }}
        >{children}</motion.div>
    );
}

export default function BlogsPage() {
    const cn = 'blogsPage';

    const seo = useSEO({
        title:       'Blog — Tips, Updates & Support Insights',
        description: 'Best practices, product updates, and stories from teams using KaizIQ Resolve. Learn how to build better support operations.',
        canonical:   'https://resolve.kaiziq.io/#/blogs',
        schema: {
            '@context': 'https://schema.org',
            '@type':    'Blog',
            name:       'KaizIQ Resolve Blog',
            url:        'https://resolve.kaiziq.io/#/blogs',
            description: 'Insights on ticket management, WhatsApp support, SLA best practices, and product updates.',
            blogPost: POSTS.map(p => ({
                '@type':       'BlogPosting',
                headline:      p.title,
                description:   p.excerpt,
                datePublished: p.date,
                author:        { '@type': 'Organization', name: 'KaizIQ Technologies' },
            })),
        },
    });

    return (
        <div className={cn}>
            {seo}
            <TopPanel />

            <section className={cn + '__hero'}>
                <FloatingPaths position={1} />
                <FloatingPaths position={-1} />
                <div className={cn + '__hero__inner'}>
                    <motion.div className={cn + '__hero__tag'} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                        Blog
                    </motion.div>
                    <motion.h1 className={cn + '__hero__title'} initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1 }}>
                        Insights for support-driven teams
                    </motion.h1>
                    <motion.p className={cn + '__hero__sub'} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}>
                        Best practices, product updates, and stories from the teams using KaizIQ Resolve.
                    </motion.p>
                </div>
            </section>

            <section className={cn + '__posts'}>
                {/* Featured */}
                {POSTS.filter(p => p.featured).map((post, i) => {
                    const tagStyle = TAG_COLORS[post.tagColor] || TAG_COLORS.blue;
                    return (
                        <Reveal key={i} className={cn + '__featured'}>
                            <div className={cn + '__featured__meta'}>
                                <span className={cn + '__post__tag'} style={{ background: tagStyle.bg, color: tagStyle.text }}>{post.tag}</span>
                                <span className={cn + '__featured__label'}>Featured</span>
                            </div>
                            <h2 className={cn + '__featured__title'}>{post.title}</h2>
                            <p className={cn + '__featured__excerpt'}>{post.excerpt}</p>
                            <div className={cn + '__post__footer'}>
                                <span className={cn + '__post__date'}><Calendar size={13} />{post.date}</span>
                                <span className={cn + '__post__read'}><Clock size={13} />{post.readTime}</span>
                                <button className={cn + '__post__cta'}>Read Article <ArrowRight size={14} /></button>
                            </div>
                        </Reveal>
                    );
                })}

                {/* Grid */}
                <div className={cn + '__grid'}>
                    {POSTS.filter(p => !p.featured).map((post, i) => {
                        const tagStyle = TAG_COLORS[post.tagColor] || TAG_COLORS.blue;
                        return (
                            <Reveal key={i} className={cn + '__post'} delay={i * 0.08}>
                                <div className={cn + '__post__top'}>
                                    <span className={cn + '__post__tag'} style={{ background: tagStyle.bg, color: tagStyle.text }}>{post.tag}</span>
                                </div>
                                <h3 className={cn + '__post__title'}>{post.title}</h3>
                                <p className={cn + '__post__excerpt'}>{post.excerpt}</p>
                                <div className={cn + '__post__footer'}>
                                    <span className={cn + '__post__date'}><Calendar size={12} />{post.date}</span>
                                    <span className={cn + '__post__read'}><Clock size={12} />{post.readTime}</span>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            <Footer />
        </div>
    );
}
